import { useState } from 'react'
import { formatMoney } from '@najah/core'
import { cn } from '@najah/ui'

export interface BarDatum {
  key: string
  label: string
  value: number
  /** سطر ثانوي تحت التسمية (الرمز، الكمية…) */
  sublabel?: string
}

interface Props {
  data: BarDatum[]
  /** لون واحد: سلسلة واحدة لا تحتاج وسيلة إيضاح — العنوان يسمّيها */
  color?: string
  format?: (v: number) => string
  emptyText?: string
  maxBars?: number
}

/**
 * أشرطة أفقية. الأفقي مقصود: أسماء المنتجات العربية طويلة،
 * والأعمدة الرأسية تُجبرها على الدوران أو القصّ.
 * الشريط ينمو من اليمين (خطّ الأساس) نحو اليسار، مع اتجاه القراءة.
 */
export function BarChart({
  data,
  color = 'var(--chart-1)',
  format = formatMoney,
  emptyText = 'لا توجد بيانات',
  maxBars = 10,
}: Props) {
  const [hover, setHover] = useState<string | null>(null)

  if (!data.length) {
    return (
      <div className="grid h-40 place-items-center rounded-lg border border-dashed border-border text-sm text-fg-muted">
        {emptyText}
      </div>
    )
  }

  const rows = data.slice(0, maxBars)
  const max = Math.max(...rows.map((r) => r.value), 1)

  return (
    <ul className="space-y-3">
      {rows.map((row) => {
        const pct = (row.value / max) * 100
        return (
          <li
            key={row.key}
            onMouseEnter={() => setHover(row.key)}
            onMouseLeave={() => setHover(null)}
            className="group"
          >
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="min-w-0 flex-1 truncate text-sm text-fg" title={row.label}>
                {row.label}
              </span>
              {/* التسمية المباشرة عند طرف الشريط — القيمة بلون النصّ لا بلون السلسلة */}
              <span className="shrink-0 text-sm font-semibold tabular-nums text-fg" dir="ltr">
                {format(row.value)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* المسار بلون سطح أفتح؛ الشريط نفسه ≤ ٨ بكسل سماكة وطرفه مستدير */}
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                <div
                  className={cn(
                    'h-full rounded-e-none rounded-s-full transition-[filter] duration-150',
                    hover === row.key && 'brightness-110',
                  )}
                  style={{ width: `${pct}%`, background: color, marginInlineStart: 'auto' }}
                />
              </div>
              {row.sublabel && (
                <span className="shrink-0 font-mono text-[10px] text-fg-subtle" dir="ltr">
                  {row.sublabel}
                </span>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
